import { Field, formValueSelector, reduxForm } from 'redux-form'

import WrappedRadio from 'components/Shared/Forms/WrappedRadio'
import { connect } from 'react-redux'
import { useFlags } from 'flags'
import WrappedInput from 'components/Shared/Forms/WrappedInput'
import WrappedSelect from 'components/Shared/Forms/WrappedSelect'
import Button from '../Shared/Button'
import WizardFooter from './WizardFooter'
import { FORM_NAME } from './shared'
import PersonaOptions from './PersonaOptions'

type WizardFormGoalPageProps = {
  handleSubmit: () => void
  handlePreviousPage: () => void
  personalEmail: boolean
}

type FormValues = {
  independent: true | false | null
  persona: string | null
  established: true | false | null
  businessName: string | null
  website: string | null
  type: string | null
  users: string | null
  description: string | null
  timeline: string | null
  budget: number | null
}

const WizardFormGoalPage = (
  props: WizardFormGoalPageProps & FormValues
): JSX.Element => {
  const { aiAssistant: aiAssistantFlag } = useFlags()

  const {
    handleSubmit,
    independent,
    persona,
    established,
    personalEmail,
    businessName,
    website,
    type,
    users,
    description,
    timeline,
    budget,
    handlePreviousPage,
  } = props
  // If personal email, they should still have the option to hire someone from the expert marketplace, but not get a meeting with Glen

  const isBusiness =
    independent === false && established === true && !personalEmail
  const businessDetails =
    businessName &&
    website &&
    type &&
    users &&
    description &&
    timeline &&
    budget

  return (
    <>
      <form
        id="new-app-goal-form"
        className="new-app-form"
        onSubmit={handleSubmit}
      >
        <div className="new-app-goal-settings">
          <div className="new-app-goal-label">
            What are you looking to achieve with Adalo?
          </div>
          <Field
            className="new-app-hiring-option"
            component={WrappedRadio}
            styled
            name="independent"
            radioValue
            label="I want to build an app myself"
          />
          <Field
            className="new-app-hiring-option"
            component={WrappedRadio}
            styled
            name="independent"
            radioValue={false}
            label="I want to hire someone to build it for me"
          />
          {independent && (
            <>
              <div className="new-app-goal-label">
                Which of these describes you best?
              </div>

              <PersonaOptions />
            </>
          )}
          {independent === false && !personalEmail && (
            <>
              <div className="new-app-goal-label">
                Do you have an established business?
              </div>

              <Field
                className="new-app-hiring-option"
                component={WrappedRadio}
                styled
                name="established"
                radioValue
                label="Yes"
              />
              <Field
                className="new-app-hiring-option"
                component={WrappedRadio}
                styled
                name="established"
                radioValue={false}
                label="No"
              />
            </>
          )}
          {established === true && independent === false && !personalEmail && (
            <>
              <div className="new-app-goal-label">
                Tell us more about your business!
              </div>

              <Field
                name="businessName"
                component={WrappedInput}
                placeholder="Adalo"
                label="Business Name"
                type="text"
              />

              <Field
                name="website"
                component={WrappedInput}
                placeholder="www.adalo.com"
                label="Website"
                type="text"
              />

              <Field
                name="type"
                component={WrappedSelect}
                placeholder="Select a type"
                label="Type"
                options={[
                  { label: 'Customer-facing', value: 'customer-facing' },
                  { label: 'Internal tool', value: 'internal-tool' },
                  { label: 'Other', value: 'other' },
                ]}
              />

              <Field
                name="users"
                component={WrappedInput}
                placeholder="100"
                label="Expected Number of Users"
                type="text"
              />

              <Field
                name="description"
                component={WrappedInput}
                placeholder="I'm building an app for..."
                label="Project Description"
                type="text"
              />

              <Field
                name="timeline"
                component={WrappedInput}
                placeholder="1-2 weeks"
                label="Timeline"
                type="text"
              />

              <Field
                name="budget"
                component={WrappedSelect}
                placeholder="Select a budget"
                label="Budget"
                options={[
                  { label: '$0 - $1,000', value: 1 },
                  { label: '$1,000 - $3,000', value: 2 },
                  { label: '$3,000 - $10,000', value: 3 },
                  { label: '$10,000+', value: 4 },
                ]}
              />
            </>
          )}
        </div>
      </form>
      <WizardFooter>
        {aiAssistantFlag === true && handlePreviousPage ? (
          <Button className="previous" onClick={handlePreviousPage}>
            Back
          </Button>
        ) : null}
        <Button
          className="next align-right"
          type="submit"
          disabled={
            independent === null ||
            (independent === false && established === null && !personalEmail) ||
            (isBusiness && !businessDetails) ||
            (independent && !persona)
          }
          form="new-app-goal-form"
          data-adalo-id="new-app-goal-form-next"
        >
          Next
        </Button>
      </WizardFooter>
    </>
  )
}

function mapStateToProps(state: Record<string, unknown>) {
  const selector = formValueSelector(FORM_NAME)
  const independentValue: unknown = selector(state, 'independent')
  const personaValue: unknown = selector(state, 'persona')
  const establishedValue: unknown = selector(state, 'established')
  const businessNameValue: unknown = selector(state, 'businessName')
  const websiteValue: unknown = selector(state, 'website')
  const typeValue: unknown = selector(state, 'type')
  const usersValue: unknown = selector(state, 'users')
  const descriptionValue: unknown = selector(state, 'description')
  const timelineValue: unknown = selector(state, 'timeline')
  const budgetValue: unknown = selector(state, 'budget')

  return {
    independent:
      typeof independentValue === 'boolean' ? independentValue : null,
    persona: personaValue as string | null,
    established:
      typeof establishedValue === 'boolean' ? establishedValue : null,
    businessName: businessNameValue as string | null,
    website: websiteValue as string | null,
    type: typeValue as string | null,
    users: usersValue as string | null,
    description: descriptionValue as string | null,
    timeline: timelineValue as string | null,
    budget: budgetValue as number | null,
  }
}

export default reduxForm<FormValues, WizardFormGoalPageProps>({
  form: FORM_NAME,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
})(connect(mapStateToProps)(WizardFormGoalPage))
